import {
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { taskAction } from 'actions';
import DeleteModal from 'components/Modal/DeleteModal';
import EditModal from 'components/Modal/EditModal';
import { RouterPromptMobile } from 'components/RouterPromptMobile';
import { TEMPORARY } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import useMobile from 'hooks/useMobile';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiPlusMedical } from 'react-icons/bi';
import { BsChatLeftDots, BsThreeDotsVertical } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Task } from 'types/reducers/task';

interface TaskListProps {
  onAddNew: () => void;
  onChange: (_task: Task) => void;
  onCloseSidebarMobile: () => void;
}

const TaskListMobile = ({ onAddNew, onChange, onCloseSidebarMobile }: TaskListProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { tasks, current } = useSelector((state) => state.task);
  const { data, total_page, current_page } = tasks;
  const { isMobile } = useMobile();

  const [recordDelete, setRecordDelete] = useState<Task>();
  const [recordEdit, setRecordEdit] = useState<Task>();
  const [selectTask, setSelectTask] = useState<Task>();

  const {
    isOpen: isOpenEditDialog,
    onOpen: onOpenEditDialog,
    onClose: onCloseEditDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const selectedTaskId = current?.task_id;
  const isChangingSelectedFiles =
    JSON.stringify(current?.selectedFiles) !== JSON.stringify(current?.initialSelectedFiles);

  const onShowMore = () => {
    dispatch(
      taskAction.getTasks({
        page: current_page + 1,
        size: 10,
      })
    );
  };

  const onClickDelete = (task: Task) => {
    setRecordDelete(task);
    onOpenDeleteDialog();
  };

  const onClickEdit = (task: Task) => {
    setRecordEdit(task);
    onOpenEditDialog();
  };

  const handleDelete = (task: Task) => {
    dispatch(
      taskAction.deleteTask({
        id: task.id,
      })
    );
  };

  const handleEdit = (updatedTask: Task & { new_name: string }) => {
    dispatch(
      taskAction.editTaskName({
        id: updatedTask.id,
        new_name: updatedTask.new_name,
      })
    );
  };

  const onChangeTask = (task: Task) => {
    if (isChangingSelectedFiles && current.type !== TEMPORARY && current.task_id !== task.id) {
      setSelectTask(task);
    } else {
      onContinueChangeTask(task);
    }
  };

  const onContinueChangeTask = (task: Task) => {
    onChange(task);
  };

  useEffect(() => {
    if (current?.task_id) {
      dispatch(taskAction.getDetailTask(current));
    }
  }, [current?.task_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isMobile && <RouterPromptMobile when={isChangingSelectedFiles} />}

      <DeleteModal
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        recordDelete={recordDelete}
        action={handleDelete}
        dataHeader={t('summary.deleteTask')}
        dataBody={t('summary.deleteTaskInstruction')}
        btnAction={t('common.delete')}
        color='red'
      />
      <EditModal
        isOpen={isOpenEditDialog}
        onClose={onCloseEditDialog}
        recordEdit={recordEdit}
        action={handleEdit}
        dataHeader={t('summary.renameTask')}
        btnAction={t('summary.rename')}
      />
      <Box bg={'bg_main_container'} borderRadius={{ lg: '20px' }}>
        <Box p={2}>
          <Flex align='center'>
            <Heading as='h3' mr='1rem' color={'white.600'} fontWeight={'bold'} size='md'>
              {t('summary.tasks')}
            </Heading>
            <Spacer />
            <Tooltip display={{ base: 'none', lg: 'block' }} label='Add new'>
              <Button
                pos='relative'
                w='40px'
                h='40px'
                bg='grey.800'
                borderRadius='50%'
                _hover={{}}
                _focus={{}}
                cursor='pointer'
                isDisabled={data?.[0]?.type === TEMPORARY}
                onClick={onAddNew}
              >
                <BiPlusMedical
                  color='white'
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    zIndex: '999',
                  }}
                />
                <Box pos='absolute' w='40px' h='40px' bg='main_summary_color' borderRadius='50%' />
              </Button>
            </Tooltip>
          </Flex>

          <Box mt={5} mb={3} ml={{ base: 5, md: 'auto' }} fontSize={12}>
            <Text>{t('common.recent')}</Text>
          </Box>
          <Box>
            {data?.map((item) => (
              <Box
                key={item.id}
                pos='relative'
                h='60px'
                my={2}
                p='12px 14px 20px 20px'
                fontSize='14px'
                fontWeight={selectedTaskId === item.id ? '600' : ''}
                textAlign='left'
                bg={
                  selectedTaskId === item.id
                    ? 'bg_conversation_item_select'
                    : 'bg_conversation_item'
                }
                borderRadius='10px'
                shadow={
                  selectedTaskId === item.id ? 'shadow_box' : 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px'
                }
                _hover={{
                  bg: 'bg_conversation_item_select',
                  boxShadow: 'shadow_box',
                }}
                cursor='pointer'
                onClick={() => {
                  onChangeTask(item);
                  onCloseSidebarMobile();
                }}
              >
                <Flex align='center' justify='between'>
                  <Box w='20px'>
                    <BsChatLeftDots size={20} />
                  </Box>
                  <Tooltip label={item.name} placement='top-start'>
                    <Box
                      overflow='hidden'
                      maxW='160px'
                      ms='12px'
                      whiteSpace='nowrap'
                      textOverflow='ellipsis'
                    >
                      {item.name}
                    </Box>
                  </Tooltip>
                  <Spacer />
                  <Box w='20px'>
                    <TaskOptions
                      data={item}
                      onClickDelete={onClickDelete}
                      onClickEdit={onClickEdit}
                    />
                  </Box>
                </Flex>
                <Box pos='absolute' right='42px' bottom='4px' fontSize='12px'>
                  {timeFormat(item?.last_updated)}
                </Box>
              </Box>
            ))}
          </Box>

          {current_page + 1 < total_page && (
            <Box
              fontSize='12px'
              textAlign='right'
              textDecoration='underline'
              cursor='pointer'
              onClick={onShowMore}
            >
              {t('common.moreHistory')}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

interface TaskOptionsProps {
  data: Task;
  onClickDelete: (_task: Task) => void;
  onClickEdit: (_task: Task) => void;
}

const TaskOptions = ({ data, onClickDelete, onClickEdit }: TaskOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Menu>
      <MenuButton
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <BsThreeDotsVertical />
      </MenuButton>
      <MenuList minW='100px' p={0} bg='transparent' borderRadius={0}>
        <MenuItem
          bg='menu_item_option'
          _hover={{ bg: 'menu_item_option_hover' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickEdit(data);
          }}
        >
          {t('common.edit')}
        </MenuItem>
        <MenuItem
          bg='menu_item_option'
          _hover={{ bg: 'menu_item_option_hover' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(data);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default TaskListMobile;
